import '../public/app-nav.css';

export type AppNavKey =
  | 'home'
  | 'chat'
  | 'tax'
  | 'socialInsurance'
  | 'faq';

/** 各項目は `/svg/` 下の SVG、または短いテキストラベル。ヒット領域は CSS で共通の正方形に統一。 */
type NavItem =
  | { key: AppNavKey; href: string; ariaLabel: string; icon: string }
  | { key: AppNavKey; href: string; ariaLabel: string; label: string };

/** 表示順: ホーム(ロゴ) → 税 → 社 → チャット → ヘルプ */
const ITEMS: NavItem[] = [
  {
    key: 'tax',
    href: '/tax.html',
    ariaLabel: '税',
    label: '税'
  },
  {
    key: 'socialInsurance',
    href: '/social-insurance.html',
    ariaLabel: '社',
    label: '社'
  },
  {
    key: 'chat',
    href: '/chat.html',
    ariaLabel: 'Chat',
    icon: 'chat_bubble_24dp_E3E3E3_FILL0_wght400_GRAD0_opsz24.svg'
  },
  {
    key: 'faq',
    href: '/faq.html',
    ariaLabel: 'ヘルプ',
    icon: 'contact_support_24dp_E3E3E3_FILL0_wght400_GRAD0_opsz24.svg'
  }
];

function navItemHtml(item: NavItem): string {
  const aria = item.ariaLabel.replace(/"/g, '&quot;');
  if ('label' in item) {
    const base = `class="app-nav-link app-nav-link--label" data-app-nav="${item.key}" href="${item.href}"`;
    return `<a ${base} aria-label="${aria}"><span class="app-nav-link__label">${item.label}</span></a>`;
  }
  const base = `class="app-nav-link app-nav-link--icon" data-app-nav="${item.key}" href="${item.href}"`;
  const src = `/svg/${item.icon}`;
  return `<a ${base} aria-label="${aria}"><img src="${src}" alt="" width="24" height="24" decoding="async" class="app-nav-link__icon" /></a>`;
}

/** クライアントルーティング対象（app.html シェル内で差し替え） */
export function pathnameToNavKey(pathname: string): AppNavKey | null {
  const p = pathname.replace(/\/$/, '') || '/';
  if (p === '/home.html' || p === '/home') return 'home';
  /* 旧 Settings URL → チャットのプロフィール設定 */
  if (
    p === '/settings.html' ||
    p === '/settings' ||
    p === '/account.html' ||
    p === '/account'
  ) {
    return 'chat';
  }
  if (p === '/faq.html' || p === '/faq') return 'faq';
  if (p === '/tax.html' || p === '/tax') return 'tax';
  if (p === '/social-insurance.html' || p === '/social-insurance') return 'socialInsurance';
  if (
    p === '/chat.html' ||
    p === '/chat' ||
    p === '/community.html' ||
    p === '/community' ||
    p === '/memo' ||
    p === '/memo/index.html'
  ) {
    return 'chat';
  }
  return null;
}

export function navKeyToDocumentTitle(key: AppNavKey): string {
  switch (key) {
    case 'home':
      return 'Home';
    case 'chat':
      return 'Chat';
    case 'tax':
      return '税';
    case 'socialInsurance':
      return '社';
    case 'faq':
      return 'ヘルプ';
    default:
      return 'Azure Memo App';
  }
}

export function setAppNavActive(active: AppNavKey, mountEl?: HTMLElement | null): void {
  const root = mountEl ?? document.getElementById('app-nav-mount');
  if (!root) return;
  root.querySelectorAll<HTMLElement>('[data-app-nav]').forEach((node) => {
    const key = node.dataset.appNav as AppNavKey | undefined;
    if (!key) return;
    const isActive = key === active;
    if (node.classList.contains('app-nav-link') || node.classList.contains('app-nav-brand')) {
      node.classList.toggle('app-nav-link-active', isActive);
      if (isActive) node.setAttribute('aria-current', 'page');
      else node.removeAttribute('aria-current');
    }
  });
}

/**
 * ナビを一度だけ描画し、以降は setAppNavActive のみ（SPA 用）。
 */
export function mountAppNav(active: AppNavKey, mountId = 'app-nav-mount'): void {
  const el = document.getElementById(mountId);
  if (!el) return;

  if (el.dataset.appNavMounted !== '1') {
    const links = ITEMS.map(navItemHtml).join('');
    el.innerHTML = `
    <nav class="app-nav" aria-label="サイト内">
      <a class="app-nav-brand" data-app-nav="home" href="/home.html" aria-label="Azure Memo App（ホーム）">
        <img src="/memo/memo-logo.svg" alt="" width="120" height="32" decoding="async" class="app-nav-logo" />
      </a>
      <div class="app-nav-links">${links}</div>
    </nav>
  `;
    el.dataset.appNavMounted = '1';
  }
  setAppNavActive(active, el);
}
